import type { RootSessionStatus } from "../rootSessionStrip";

/**
 * FD-CAN-39 — a session's FACE: the intensity its Neucleos triangle wears.
 * One hue (the Neucleos identity), three intensities — FD-CAN-13's law
 * carried to the glyph: hue is identity and never changes; intensity is
 * state. A pure fold over the rail's EXISTING status vocabulary, so nothing
 * new is stored or invented (DL3/DL4):
 *
 *   working | monitoring             → alive    the agent runs — the glyph breathes
 *   approval | input | failed | done → waiting  the person must look — steady, lit
 *   null                             → dormant  seen, nothing live — silver
 *
 * "done" already means "finished with a completion the person has not opened"
 * (shellRootSessionStatus over the device-local visit stamp), which is exactly
 * "waiting on the person"; the needs-a-person states join it because the
 * person is the one who must act. The liveness dot keeps naming WHICH.
 */
export type SessionFace = "alive" | "waiting" | "dormant";

export function sessionFace(status: RootSessionStatus | null): SessionFace {
  switch (status) {
    case "working":
    case "monitoring":
      return "alive";
    case "approval":
    case "input":
    case "limited":
    case "failed":
    case "done":
      return "waiting";
    case null:
      return "dormant";
  }
}

/** The words a face speaks in a tooltip or title. Dormant is silent: the absence of a signal is not a signal. */
export const SESSION_FACE_LABELS: Readonly<Record<SessionFace, string>> = {
  alive: "working",
  waiting: "waiting on you",
  dormant: "dormant",
};

/**
 * The breakdown words for a slice's sessions — what the tight chip's tooltip
 * and the wide row's title carry beside the label: "2 sessions · 1 working ·
 * 1 waiting on you". A single session drops the counts ("working"); a slice
 * whose sessions are all dormant says nothing at all.
 */
export function sessionFaceBreakdown(statuses: ReadonlyArray<RootSessionStatus | null>): string {
  let alive = 0;
  let waiting = 0;
  for (const status of statuses) {
    const face = sessionFace(status);
    if (face === "alive") alive += 1;
    else if (face === "waiting") waiting += 1;
  }
  const plural = statuses.length > 1;
  const word = (count: number, face: Exclude<SessionFace, "dormant">) =>
    plural ? `${count} ${SESSION_FACE_LABELS[face]}` : SESSION_FACE_LABELS[face];
  const parts: string[] = [];
  if (plural) parts.push(`${statuses.length} sessions`);
  if (alive > 0) parts.push(word(alive, "alive"));
  if (waiting > 0) parts.push(word(waiting, "waiting"));
  return parts.join(" · ");
}
